'use client';
import React, { useState } from 'react';
import { Button, Grow, Typography, Fade } from '@mui/material';
import OptionSelectedFeedback from './elements/option-selected-feedback';
import Stepper from './elements/stepper';
import Heading from './elements/heading';
import { CreativeRadio } from '@/shared/ui';
import { OnboardingEventKeys, OnboardingEvents } from '../events';
import { EventBus } from '@/shared/utils';
import { useOnboarding } from '../hooks/use-onboarding';

interface StudySessionsProps {
  // Definisci qui i tuoi tipi di prop, ad esempio:
  eventBus: EventBus<OnboardingEvents>;
}

const StudySessions = ({ eventBus }: StudySessionsProps) => {
  const [selectedValue, setSelectedValue] = useState<string>('');
  const { studySessionsOptions, studySessionsDoHandler, profileStudySessions } =
    useOnboarding(eventBus);

  React.useEffect(() => {
    if (profileStudySessions) {
      setSelectedValue(profileStudySessions.description);
    }
  }, [profileStudySessions]);

  // Handler for radio button selection
  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedValue(event.target.value);
    studySessionsDoHandler({ description: event.target.value });
  };

  // Find the feedback content for the selected option
  const selectedOption = studySessionsOptions?.find((option) => option.name === selectedValue);

  const handleContinue = () => {
    if (!profileStudySessions || !profileStudySessions.description) return;
    // Emettiamo l'evento di selezione padre
    eventBus.emit(OnboardingEventKeys.studySessions.selection.do, profileStudySessions);
  };

  return (
    <>
      {/* Stepper */}
      <Fade in={true}>
        <div>
          <Stepper
            activeStep={1}
            onBack={() => eventBus.emit(OnboardingEventKeys.instrument.selection.view, undefined)}
          />
        </div>
      </Fade>

      <section className="flex flex-col pt-10 pb-12 gap-y-8 items-center max-w-[750px] m-auto text-center">
        {/* Heading */}
        <Fade
          in={true}
          style={{
            transitionDuration: '700ms',
          }}
        >
          <div>
            <Heading
              title="Quante volte vuoi esercitarti ogni settimana?"
              subtitle="Diamo forma e spazio alla tua musica."
            />
          </div>
        </Fade>

        {/* Options to select */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-[650px] w-full">
          {/* CreativeRadio */}
          {studySessionsOptions?.map((sessionOption) => (
            <Grow
              key={sessionOption.name}
              in={true}
              style={{
                transformOrigin: 'center center',
                transitionDelay: '50ms',
              }}
            >
              <div>
                <CreativeRadio
                  imageSrc={sessionOption.imageSrc}
                  altText={sessionOption.altText}
                  name={sessionOption.name}
                  value={sessionOption.name}
                  checked={selectedValue === sessionOption.name}
                  onChange={handleRadioChange}
                />
              </div>
            </Grow>
          ))}
        </div>

        {/* Button */}
        <Grow in={true}>
          <div>
            <Button
              size="large"
              variant="contained"
              color="primary"
              className="max-w-full w-[220px]"
              disabled={!selectedOption}
              onClick={handleContinue}
            >
              <Typography variant="button-l">Continua</Typography>
            </Button>
          </div>
        </Grow>

        {selectedOption && (
          <Grow
            in={!!selectedOption} // Ensure it's a boolean
            style={{
              transformOrigin: 'top center',
              transitionDelay: '125ms',
            }}
          >
            <div>
              <OptionSelectedFeedback
                imageSrc={selectedOption.feedback.imageSrc}
                text={selectedOption.feedback.text}
                boxClassName="bg-paper"
              />
            </div>
          </Grow>
        )}
      </section>
    </>
  );
};

export default StudySessions;
